import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton, Link } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { useAccessTicketLinkImportRowLink } from 'src/app/hooks/ticket-link-imports/useAccessTicketLinkImportRowLink';
import EventUrlService from 'src/app/pages/events/eventUrlService';
import Toaster from 'src/app/utilities/helpers/Toaster';
import {
    TicketLinkImportRow,
    TicketLinkImportRowStatus,
} from 'src/data/models/ticket-link-import/TicketLinkImport';
import Button from 'src/view/components/button/Button';
import { StatusChip, StatusChipVariantType } from 'src/view/components/status-label/StatusChip';
import Table from 'src/view/components/table/table/Table';
import {
    NewTableColumn,
    TABLE_CELL_WIDTH,
    TablePaginationData,
} from 'src/view/components/table/table/Types';

function getStatusVariant(status: TicketLinkImportRowStatus): StatusChipVariantType {
    switch (status) {
        case 'Applied':
            return 'green';
        case 'Matched':
            return 'blue';
        case 'ApplyFailed':
        case 'MatchingFailed':
            return 'red';
        case 'Pending':
        default:
            return 'grey';
    }
}

export interface TicketLinkImportRowTableProps {
    eventId: string;
    ticketLinkImportRows: TicketLinkImportRow[];
    loading: boolean;
    onClickDuplicateFile: (rowId: string) => void;
    pagination: TablePaginationData;
}

export default function TicketLinkImportRowTable(
    props: Readonly<TicketLinkImportRowTableProps>
): ReactElement {
    const { mutate: copyTicketLink, isLoading: copyLoading } = useAccessTicketLinkImportRowLink(
        (result) => {
            const url = result.data.data.url;
            navigator.clipboard.writeText(url).then((_) => {
                Toaster.toast('Copied ticket link!', { variant: 'success' });
            });
        }
    );

    const columns = useMemo<NewTableColumn<TicketLinkImportRow>[]>(
        () => [
            {
                key: 'categoryName',
                title: 'Seating plan category',
                cellRenderer: (row) => <span>{row.categoryName}</span>,
                width: TABLE_CELL_WIDTH.LARGE,
            },
            {
                key: 'block',
                title: 'Block',
                cellRenderer: (row) => <span>{row.block}</span>,
                width: TABLE_CELL_WIDTH.SMALL,
            },
            {
                key: 'row',
                title: 'Row',
                cellRenderer: (importRow) => <span>{importRow.row}</span>,
                width: TABLE_CELL_WIDTH.SMALL,
            },
            {
                key: 'seat',
                title: 'Seat',
                cellRenderer: (row) => <span>{row.seat}</span>,
                width: TABLE_CELL_WIDTH.SMALL,
            },
            {
                title: 'Link',
                titleAlign: 'center',
                align: 'center',
                width: TABLE_CELL_WIDTH.TINY,
                cellRenderer: (row) => {
                    return (
                        <Button
                            disableRipple={false}
                            disabled={copyLoading}
                            variant="text"
                            onClick={() => {
                                copyTicketLink(row.id);
                            }}
                        >
                            Copy
                        </Button>
                    );
                },
            },
            {
                key: 'status',
                title: 'Status',
                cellRenderer: (row) => (
                    <StatusChip variant={getStatusVariant(row.status)} text={row.status} />
                ),
                width: TABLE_CELL_WIDTH.MEDIUM,
            },
            {
                key: 'reason',
                title: 'Failure reason',
                cellRenderer: (row) => (
                    <>
                        <span>{row.reason ? row.reason : '-'}</span>
                        {row.reason === 'DuplicateLink' && (
                            <IconButton
                                color="primary"
                                onClick={() => props.onClickDuplicateFile(row.id)}
                            >
                                <OpenInNewIcon />
                            </IconButton>
                        )}
                    </>
                ),
                width: TABLE_CELL_WIDTH.MEDIUM,
            },
            {
                key: 'updatedAt',
                title: 'Last Updated',
                cellRenderer: (row) => (
                    <span>{new Date(row.updatedAt).toLocaleString()}</span>
                ),
                width: TABLE_CELL_WIDTH.MEDIUM,
            },
            {
                align: 'right',
                width: TABLE_CELL_WIDTH.SMALL,
                cellRenderer: (row) => (
                    <>
                        {row.ticketId && (
                            <Link
                                target="_blank"
                                href={EventUrlService.tickets(props.eventId, {
                                    ticketId: row.ticketId,
                                })}
                            >
                                Go to ticket
                            </Link>
                        )}
                    </>
                ),
            },
        ],
        [props.eventId, props.onClickDuplicateFile]
    );

    return (
        <Table<TicketLinkImportRow, TicketLinkImportRow>
            data={props.ticketLinkImportRows}
            rowIdResolver={(rowData: TicketLinkImportRow) => ({
                id: rowData.id,
                data: { ...rowData },
            })}
            columns={columns}
            loading={props.loading}
            pagination={props.pagination}
            minHeight={425}
        />
    );
}
